import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { BaseLayout } from "../layouts/BaseLayout";
import { PageContainer } from "../components/PageContainer/PageContainer";
import { AlbumRecommendSidebar } from "../components/AlbumRecommendSidebar/AlbumRecommendSidebar";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { H1, Paragraph } from "../components/Typography/Typography";
import { fetchAlbumById, searchPath, Album } from "../api/albums";

import "./AlbumPage.scss";

export function AlbumPage() {
    const { albumId } = useParams<{ albumId: string }>();
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("q") ?? "";
    const [album, setAlbum] = React.useState<Album | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const loadAlbum = async () => {
            if (!albumId || !/^\d+$/.test(albumId)) {
                setLoadError("Invalid album URL.");
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setLoadError(null);

            try {
                const data = await fetchAlbumById(albumId);
                setAlbum(data);
            } catch (error) {
                console.error("Error loading album:", error);
                setLoadError("Could not load this album.");
                setAlbum(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadAlbum();
    }, [albumId]);

    return (
        <BaseLayout className="album-page">
            <PageContainer>
                <Paragraph className="album-page__back" variant="body-2">
                    <Link to={searchPath(searchQuery)}>&larr; Search</Link>
                </Paragraph>

                {isLoading && <LoadingSpinner />}

                {!isLoading && loadError && (
                    <Paragraph variant="body-2" color="mid-contrast">
                        {loadError}
                    </Paragraph>
                )}

                {!isLoading && album && (
                    <div className="album-page__layout">
                        <section className="album-page__main" aria-label="Album details">
                            {album.source_cover_url ? (
                                <img
                                    className="album-page__cover"
                                    src={album.source_cover_url}
                                    alt=""
                                />
                            ) : (
                                <div className="album-page__cover album-page__cover--placeholder" />
                            )}

                            <div className="album-page__details">
                                <H1 variant="heading-2" fontFamily="sans-serif">
                                    {album.title}
                                </H1>
                                <Paragraph variant="body-1" color="mid-contrast">
                                    {album.artist_name}
                                </Paragraph>

                                {album.source_url && (
                                    <a
                                        className="album-page__external-link"
                                        href={album.source_url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        Open on Spotify
                                    </a>
                                )}
                            </div>
                        </section>

                        <AlbumRecommendSidebar album={album} />
                    </div>
                )}
            </PageContainer>
        </BaseLayout>
    );
}
